import * as THREE from "three";
import type { FrameContext, RuntimeSystem, WorldQuery } from "../core/types";

export type NaturalFeedbackKind = "stone-step" | "earth-step" | "wade" | "vegetation-brush";

export interface NaturalFeedbackCue {
  kind: NaturalFeedbackKind;
  intensity: number;
  position: THREE.Vector3;
}

export interface NaturalInteractionFeedbackOptions {
  scene: THREE.Scene;
  camera: THREE.Camera;
  world: WorldQuery;
  onCue?: (cue: NaturalFeedbackCue) => void;
  reducedMotion?: boolean;
}

export interface MovementFeedbackSample {
  x: number;
  z: number;
  surface: "stone" | "earth" | "grass" | "water";
  speed: number;
}

const STEP_DISTANCE: Record<MovementFeedbackSample["surface"], number> = {
  stone: 0.76,
  earth: 0.72,
  grass: 0.66,
  water: 0.58,
};

export class MovementFeedbackTracker {
  private lastX: number | null = null;
  private lastZ: number | null = null;
  private accumulatedDistance = 0;

  sample(sample: MovementFeedbackSample): NaturalFeedbackKind | null {
    if (this.lastX === null || this.lastZ === null) {
      this.lastX = sample.x;
      this.lastZ = sample.z;
      return null;
    }

    const distance = Math.hypot(sample.x - this.lastX, sample.z - this.lastZ);
    this.lastX = sample.x;
    this.lastZ = sample.z;
    if (distance > 3) {
      this.accumulatedDistance = 0;
      return null;
    }
    if (sample.speed < 0.08) return null;

    this.accumulatedDistance += distance;
    const threshold = STEP_DISTANCE[sample.surface];
    if (this.accumulatedDistance < threshold) return null;
    this.accumulatedDistance %= threshold;
    if (sample.surface === "water") return "wade";
    if (sample.surface === "grass") return "vegetation-brush";
    return sample.surface === "earth" ? "earth-step" : "stone-step";
  }

  reset(): void {
    this.lastX = null;
    this.lastZ = null;
    this.accumulatedDistance = 0;
  }
}

interface RippleEffect {
  mesh: THREE.Mesh<THREE.RingGeometry, THREE.MeshBasicMaterial>;
  age: number;
  active: boolean;
}

interface BrushEffect {
  group: THREE.Group;
  material: THREE.MeshBasicMaterial;
  age: number;
  active: boolean;
}

const RIPPLE_LIFETIME = 0.9;
const BRUSH_LIFETIME = 0.48;

export class NaturalInteractionFeedback implements RuntimeSystem {
  readonly root = new THREE.Group();

  private readonly scene: THREE.Scene;
  private readonly camera: THREE.Camera;
  private readonly world: WorldQuery;
  private readonly onCue?: (cue: NaturalFeedbackCue) => void;
  private readonly reducedMotion: boolean;
  private readonly tracker = new MovementFeedbackTracker();
  private readonly previousPosition = new THREE.Vector3();
  private readonly cuePosition = new THREE.Vector3();
  private readonly rippleGeometry = new THREE.RingGeometry(0.16, 0.2, 24);
  private readonly brushGeometry = new THREE.PlaneGeometry(0.045, 0.34);
  private readonly ripples: RippleEffect[] = [];
  private readonly brushes: BrushEffect[] = [];
  private initialized = false;
  private disposed = false;

  constructor(options: NaturalInteractionFeedbackOptions) {
    this.scene = options.scene;
    this.camera = options.camera;
    this.world = options.world;
    this.onCue = options.onCue;
    this.reducedMotion = options.reducedMotion ?? false;
    this.root.name = "natural-interaction-feedback";
    this.root.renderOrder = 6;
    this.scene.add(this.root);
    this.createRipplePool(6);
    this.createBrushPool(4);
  }

  update(frame: FrameContext): void {
    if (this.disposed) return;
    this.updateEffects(Math.min(Math.max(frame.delta, 0), 0.1));
    if (frame.paused) {
      this.tracker.reset();
      this.initialized = false;
      return;
    }

    const position = this.camera.position;
    if (!this.initialized) {
      this.previousPosition.copy(position);
      this.initialized = true;
    }
    const distance = Math.hypot(
      position.x - this.previousPosition.x,
      position.z - this.previousPosition.z,
    );
    const speed = frame.delta > 0 ? distance / frame.delta : 0;
    this.previousPosition.copy(position);
    const waterDepth = Math.max(0, this.world.getWaterDepth(position.x, position.z));
    const surface = waterDepth > 0.025
      ? "water"
      : this.world.getSurfaceKind(position.x, position.z);
    const kind = this.tracker.sample({ x: position.x, z: position.z, surface, speed });
    if (!kind) return;

    const intensity = THREE.MathUtils.clamp(speed / 3.1, 0.35, 1);
    const groundHeight = this.world.getHeight(position.x, position.z);
    this.cuePosition.set(position.x, groundHeight + (surface === "water" ? waterDepth : 0.03), position.z);
    if (kind === "wade") this.spawnRipple(this.cuePosition);
    if (kind === "vegetation-brush") this.spawnBrush(this.cuePosition);
    this.onCue?.({ kind, intensity, position: this.cuePosition.clone() });
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.scene.remove(this.root);
    this.rippleGeometry.dispose();
    this.brushGeometry.dispose();
    for (const ripple of this.ripples) ripple.mesh.material.dispose();
    for (const brush of this.brushes) brush.material.dispose();
    this.root.clear();
  }

  private createRipplePool(size: number): void {
    for (let index = 0; index < size; index += 1) {
      const material = new THREE.MeshBasicMaterial({
        color: 0xb8d4d0,
        transparent: true,
        opacity: 0,
        depthWrite: false,
        side: THREE.DoubleSide,
      });
      const mesh = new THREE.Mesh(this.rippleGeometry, material);
      mesh.name = `wading-ripple-${index + 1}`;
      mesh.rotation.x = -Math.PI / 2;
      mesh.visible = false;
      mesh.renderOrder = 6;
      this.root.add(mesh);
      this.ripples.push({ mesh, age: 0, active: false });
    }
  }

  private createBrushPool(size: number): void {
    for (let index = 0; index < size; index += 1) {
      const material = new THREE.MeshBasicMaterial({
        color: 0x78905c,
        transparent: true,
        opacity: 0,
        depthWrite: false,
        side: THREE.DoubleSide,
      });
      const group = new THREE.Group();
      group.name = `vegetation-brush-${index + 1}`;
      for (let bladeIndex = 0; bladeIndex < 3; bladeIndex += 1) {
        const blade = new THREE.Mesh(this.brushGeometry, material);
        blade.position.set((bladeIndex - 1) * 0.085, 0.17, (bladeIndex % 2) * 0.045);
        blade.rotation.y = bladeIndex * 1.9;
        group.add(blade);
      }
      group.visible = false;
      this.root.add(group);
      this.brushes.push({ group, material, age: 0, active: false });
    }
  }

  private spawnRipple(position: THREE.Vector3): void {
    const effect = this.ripples.find((candidate) => !candidate.active) ?? this.ripples[0];
    if (!effect) return;
    effect.active = true;
    effect.age = 0;
    effect.mesh.visible = true;
    effect.mesh.position.copy(position);
    effect.mesh.scale.setScalar(0.65);
    effect.mesh.material.opacity = 0.46;
  }

  private spawnBrush(position: THREE.Vector3): void {
    const effect = this.brushes.find((candidate) => !candidate.active) ?? this.brushes[0];
    if (!effect) return;
    effect.active = true;
    effect.age = 0;
    effect.group.visible = !this.reducedMotion;
    effect.group.position.copy(position);
    effect.group.rotation.set(0, this.camera.rotation.y, 0.24);
    effect.group.scale.setScalar(1);
    effect.material.opacity = 0.42;
  }

  private updateEffects(delta: number): void {
    for (const effect of this.ripples) {
      if (!effect.active) continue;
      effect.age += delta;
      const progress = effect.age / RIPPLE_LIFETIME;
      effect.mesh.scale.setScalar(0.65 + progress * 3.2);
      effect.mesh.material.opacity = Math.max(0, (1 - progress) * 0.46);
      if (progress >= 1) {
        effect.active = false;
        effect.mesh.visible = false;
      }
    }
    for (const effect of this.brushes) {
      if (!effect.active) continue;
      effect.age += delta;
      const progress = effect.age / BRUSH_LIFETIME;
      effect.group.rotation.z = 0.24 * (1 - progress);
      effect.group.scale.y = 1 - Math.sin(progress * Math.PI) * 0.22;
      effect.material.opacity = Math.max(0, (1 - progress) * 0.42);
      if (progress >= 1) {
        effect.active = false;
        effect.group.visible = false;
      }
    }
  }
}
